import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../store/store';
import { ACCOUNT_TYPES, ACCOUNT_VERIF_TYPES } from '../../utils/constants/auth';
import { EXTERNAL_LINKS, paths, routes } from '../../utils/constants/routes';
import { Link } from 'react-router';
import { useEffect } from 'react';
import MobileAppRefresher from '../loaders/MobileAppRefresher';
import { matchPath } from '../../utils/utils';
import useHistory from '../../hooks/useHistory';
import { Tooltip } from 'react-tooltip';
import pubSub from '../../utils/pubsub';
import { isOutreachDisabled } from '../../utils/constants/variables';

const MobileNavigation = observer(() => {
  const {
    utilsStore,
    authStore,
    outreachStore,
    projectStore,
    profilerProjectStore,
    profilerAccessStore
  } = useStore();
  const { location, navigate } = useHistory();

  const { userAccountType, user, logout, userVerificationStatus, hasUserProfileCompany, userInfo } =
    authStore;

  const isAIAgentEnabled = authStore.userInfo?.isCognitoAIAgentEnabled || false;

  const state = useLocalObservable(() => ({
    isLogoutConfirmationOpen: false,
    setIsLogoutConfirmationOpen: (value = false) => (state.isLogoutConfirmationOpen = value),
    closeOnDesktopResize: () => {
      utilsStore.setIsMobileHamburgerOpen();
    }
  }));

  const profilerAccessTooltipContent = (
    <>
      Need full platform access?
      <br />
      Contact <a href="mailto:support@transact.digital">support@transact.digital</a>
      <br />
      for assistance.
    </>
  );

  const disabledOutreachTooltipContent = <>Upcoming feature</>;

  // Legal pages live on td-website (see EXTERNAL_LINKS) and the third-party notices are a
  // static build file; render them as external anchors that open in a new tab, not router
  // links (the in-app routes were removed).
  const LOGOUT_ITEMS = [
    { href: EXTERNAL_LINKS.TERMS_N_CONDITIONS, name: 'Terms & conditions', external: true },
    { href: EXTERNAL_LINKS.PRIVACY_POLICY, name: 'Privacy Policy', external: true },
    {
      href: EXTERNAL_LINKS.DATA_PROCESSING_AGREEMENT,
      name: 'Data processing agreement',
      external: true
    },
    { href: EXTERNAL_LINKS.IMPRINT, name: 'Imprint', external: true },
    { href: EXTERNAL_LINKS.THIRD_PARTY_NOTICES, name: 'Third-party notices', external: true }
  ];

  const LOGIN_ITEMS = [
    // { href: paths.HOME_FULL, name: 'Home', isActive: matchPath([paths.HOME_FULL], location) },
    {
      href: paths.USER_PROFILE,
      name: 'My account',
      isActive: matchPath([routes.USER_PROFILE_PATH], location)
    },
    {
      href: paths.COMPANY_PROFILE,
      name: 'My company',
      isActive: matchPath([paths.COMPANY_PROFILE], location)
    },
    {
      href: paths.AGENT,
      name: 'Agentic AI',
      isActive: matchPath([paths.AGENT], location),
      isDisabled: !isAIAgentEnabled,
      tooltipContent: !isAIAgentEnabled
        ? 'Need access to Agentic AI? Contact support@transact.digital for assistance.'
        : null
    },
    {
      href: paths.PROFILER,
      name: 'Search',
      profilerOnly: true,
      investorOnly: true,
      isActive: matchPath([paths.PROFILER], location),
      isDisabled: !profilerAccessStore.canUseProfilerSearch,
      tooltipContent: !profilerAccessStore.canUseProfilerSearch
        ? profilerAccessTooltipContent
        : null,
      onClick: () => {
        if (matchPath([paths.PROFILER], location)) {
          pubSub.publish({ event: 'force-close-company-details' });
        }
      }
    },
    {
      href: paths.HOME_FULL,
      name: 'Search',
      agentOnly: true,
      isActive: matchPath([paths.HOME_FULL], location),
      onClick: () => {
        if (matchPath([paths.HOME_FULL], location)) {
          pubSub.publish({ event: 'force-close-company-details' });
        }
      }
    },
    {
      href:
        paths.PROFILER_WATCH_LIST + '?params=' + encodeURIComponent(JSON.stringify({ page: 1 })),
      name: 'Watchlist',
      profilerOnly: true,
      isActive: matchPath([paths.PROFILER_WATCH_LIST], location),
      errorNotificationDot:
        !profilerProjectStore.watchlist.length && !!profilerProjectStore.loadWatchlistError,
      isDisabled: !profilerAccessStore.canViewProfilerWatchlist,
      tooltipContent: !profilerAccessStore.canViewProfilerWatchlist
        ? profilerAccessTooltipContent
        : null,
      onClick: () => {
        if (matchPath([paths.PROFILER_WATCH_LIST], location)) {
          pubSub.publish({ event: 'force-close-company-details' });
        }
      }
    },
    {
      href:
        paths.PROFILER_PROJECTS +
        '?params=' +
        encodeURIComponent(
          JSON.stringify({
            projectTypeTab: 'saved',
            projectSort: 'DATE_DESC',
            projectTypePage: 1
          })
        ),
      name: 'Projects',
      profilerOnly: true,
      investorOnly: true,
      isActive: matchPath([paths.PROFILER_PROJECTS], location),
      notificationsCount: profilerProjectStore.projects.filter((p) =>
        profilerProjectStore.hasProjectNewFeedback(p)
      ).length,
      errorNotificationDot:
        !profilerProjectStore.allProjects.length && !!profilerProjectStore.loadProjectsError,
      isDisabled: !profilerAccessStore.canUseProfilerSearch,
      tooltipContent: !profilerAccessStore.canUseProfilerSearch
        ? profilerAccessTooltipContent
        : null
    },
    {
      href:
        paths.PROFILER_TEAM +
        '?params=' +
        encodeURIComponent(JSON.stringify({ projectTypeTab: 'pending' })),
      name: 'Team',
      investorOnly: true,
      profilerOnly: true,
      isActive: matchPath([paths.PROFILER_TEAM], location),
      notificationDot: profilerProjectStore.pendingSharedProjects.length > 0,
      errorNotificationDot:
        !profilerProjectStore.allProjects.length && !!profilerProjectStore.loadProjectsError,
      isDisabled: !profilerAccessStore.hasAnyProfilerAccess,
      tooltipContent: !profilerAccessStore.hasAnyProfilerAccess
        ? profilerAccessTooltipContent
        : null
    },
    {
      href: paths.FUNDS,
      name: 'My funds & portfolios',
      investorOnly: true,
      nonCorporatesOnly: true,
      isActive: matchPath([routes.FUNDS_PATH, routes.PORTFOLIO_COMPANIES_PATH], location)
    },
    {
      href: paths.INVEST_PROFILES,
      name: 'My invest profiles',
      investorOnly: true,
      corporatesOnly: true,
      isActive: matchPath([routes.INVEST_PROFILES_PATH], location)
    },
    {
      href:
        paths.ALL_INVESTMENTS +
        '?params=' +
        (authStore.isCoraporatesInvestor
          ? encodeURIComponent(JSON.stringify({ tab: 'investprofiles', page: 1 }))
          : encodeURIComponent(JSON.stringify({ tab: 'funds', page: 1 }))),
      name: 'All investments',
      investorOnly: true,
      masterOnly: true,
      isActive: matchPath([paths.ALL_INVESTMENTS], location)
    },
    {
      href:
        paths.PROJECTS +
        '?params=' +
        encodeURIComponent(
          JSON.stringify({
            projectTypeTab: 'saved',
            projectSort: 'DATE_DESC',
            projectTypePage: 1
          })
        ),
      name: 'Projects',
      agentOnly: true,
      isActive: matchPath([paths.PROJECTS], location),
      notificationsCount:
        projectStore.projects.filter((p) => projectStore.hasProjectNewFeedback(p)).length +
        profilerProjectStore.projects.filter((p) => profilerProjectStore.hasProjectNewFeedback(p))
          .length,
      errorNotificationDot:
        (!projectStore.allProjects.length && !!projectStore.loadProjectsError) ||
        (!profilerProjectStore.allProjects.length && !!profilerProjectStore.loadProjectsError)
    },
    {
      href:
        paths.TEAM + '?params=' + encodeURIComponent(JSON.stringify({ projectTypeTab: 'pending' })),
      name: 'Team',
      agentOnly: true,
      isActive: matchPath([paths.TEAM], location),
      notificationsCount:
        projectStore.pendingSharedProjects.length +
        profilerProjectStore.pendingSharedProjects.length,
      errorNotificationDot:
        (!projectStore.allProjects.length && !!projectStore.loadProjectsError) ||
        (!profilerProjectStore.allProjects.length && !!profilerProjectStore.loadProjectsError)
    },
    {
      href:
        paths.OUTREACH +
        '?params=' +
        (outreachStore.recentOutreaches[0]
          ? encodeURIComponent(
              JSON.stringify({
                selected: 'recent',
                type: null,
                outreachId:
                  utilsStore.windowWidth <= 1200 ? null : outreachStore.recentOutreaches[0].id
              })
            )
          : encodeURIComponent(
              JSON.stringify({ selected: 'recent', type: null, outreachId: null })
            )),
      name: 'Outreach',
      isActive: matchPath([paths.OUTREACH], location),
      isDisabled: isOutreachDisabled,
      tooltipContent: isOutreachDisabled ? disabledOutreachTooltipContent : null,
      notificationsCount: outreachStore.totalOutreachMessagesCount,
      errorNotificationDot: !outreachStore.outreaches.length && !!outreachStore.loadOutreachesError
    },
    {
      href: paths.ADMIN_DASHBOARD,
      name: 'Admin dashboard',
      adminOnly: true,
      isActive: matchPath([paths.ADMIN_DASHBOARD], location)
    }
  ];

  useEffect(() => {
    if (utilsStore.isMobileHamburgerOpen && state.isLogoutConfirmationOpen) {
      state.setIsLogoutConfirmationOpen();
    }
  }, [utilsStore.isMobileHamburgerOpen]);

  useEffect(() => {
    if (utilsStore.windowWidth > 740) {
      state.closeOnDesktopResize();
    }
  }, [state, utilsStore.windowWidth]);

  const renderMenuItem = (
    {
      href,
      name,
      investorOnly,
      agentOnly,
      masterOnly,
      corporatesOnly,
      nonCorporatesOnly,
      adminOnly,
      isActive,
      notificationsCount,
      notificationDot,
      errorNotificationDot,
      isDisabled,
      tooltipContent,
      onClick,
      external
    },
    idx
  ) => {
    let className = isActive ? 'selected' : '';
    let disabled = false;

    if (investorOnly && userAccountType !== ACCOUNT_TYPES.INVESTOR) {
      className += ' hidden';
    }
    if (agentOnly && userAccountType !== ACCOUNT_TYPES.AGENT) {
      className += ' hidden';
    }
    if (masterOnly && !userInfo?.isMasterUser) {
      className += ' hidden';
    }
    if (corporatesOnly && !authStore.isCoraporatesInvestor) {
      className += ' hidden';
    }
    if (nonCorporatesOnly && authStore.isCoraporatesInvestor) {
      className += ' hidden';
    }
    if (adminOnly && !authStore.userInfo?.isCognitoAdmin) {
      className += ' hidden';
    }
    if (isDisabled) {
      className += ' disabled';
      disabled = true;
    }

    const listItemId = `mn-${idx}`;

    return (
      <li key={idx} className={className}>
        {disabled && (
          <div className="pseudo-link disabled" id={listItemId}>
            {name}{' '}
          </div>
        )}
        {!disabled && external && (
          <a
            id={listItemId}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              utilsStore.setIsMobileHamburgerOpen(false, true);
              onClick?.();
            }}
          >
            {name}{' '}
          </a>
        )}
        {!disabled && !external && (
          <Link
            id={listItemId}
            to={href}
            onClick={(e) => {
              e.preventDefault();
              e.nativeEvent?.preventDefault();
              utilsStore.setIsMobileHamburgerOpen(false, true);
              onClick?.();
              navigate(href);
            }}
          >
            {name}{' '}
            {!!notificationsCount && (
              <div className={`notifications-count ${errorNotificationDot ? 'semi-error' : ''}`}>
                {notificationsCount}
              </div>
            )}
            {!notificationsCount && !!notificationDot && <div className="notifications-count" />}
            {!notificationsCount && !notificationDot && errorNotificationDot && (
              <div className="notifications-count error" />
            )}
          </Link>
        )}

        {!!tooltipContent && (
          <Tooltip
            anchorSelect={`#${listItemId}`}
            multiline={true}
            variant="dark"
            place="bottom-center"
            opacity={1}
            {...{
              children: tooltipContent
            }}
            className="mobile-profiler-access-tooltip"
            clickable
          />
        )}
      </li>
    );
  };

  const showLoginItems =
    userVerificationStatus === ACCOUNT_VERIF_TYPES.APPROVED &&
    (userInfo?.isMasterUser ? hasUserProfileCompany : true);

  return (
    <nav className={`${utilsStore.isMobileHamburgerOpen ? 'opened' : 'closed'}`}>
      {state.isLogoutConfirmationOpen ? (
        <div className="confirm-logout">
          <MobileAppRefresher isFromMobileNav />
          <div className="confirmation-dialog-wrap">
            <div className="text">Do you really want to log out?</div>
            <div className="actions">
              <a
                to="#"
                className="btn btn-primary"
                onClick={(e) =>
                  e.preventDefault() &
                  state.setIsLogoutConfirmationOpen() &
                  window.scrollTo({ top: 0 })
                }
              >
                No, stay logged in
              </a>
              <a
                className="btn btn-outline"
                onClick={() =>
                  logout() &
                  state.setIsLogoutConfirmationOpen() &
                  utilsStore.setIsMobileHamburgerOpen()
                }
              >
                Yes, log me out
              </a>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            <MobileAppRefresher isFromMobileNav />
            <ul>
              {showLoginItems && LOGIN_ITEMS.map((item, idx) => renderMenuItem(item, idx))}
              {!showLoginItems && LOGOUT_ITEMS.map((item, idx) => renderMenuItem(item, idx))}
            </ul>
          </div>
          {user && (
            <div className="logout">
              <a
                href="#"
                onClick={(e) =>
                  e.preventDefault() &
                  state.setIsLogoutConfirmationOpen(true) &
                  window.scrollTo({ top: 0 })
                }
                // id="logout-menu-button"
              >
                Log out
              </a>
            </div>
          )}
        </>
      )}
    </nav>
  );
});

export default MobileNavigation;
